import { Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { WorkshopListComponent } from './workshop-list/workshop-list.component';
import { WorkshopMapComponent } from './workshop-map/workshop-map.component';
import { WorkshopDetailComponent } from './workshop-detail/workshop-detail.component';
import { WorkshopService } from '../../services/workshop.service';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@jsverse/transloco';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { ConfirmationOptions } from '../../../../../_shared/models/modals/confirmation-options.model';
import { WorkshopDetailsResponse, WorkshopLocationResponse, WorkshopsResponse } from '../../models/workshop-response.model';
import { DeleteWorkshopRequest, WorkshopDetailsRequest } from '../../models/workshop-request.model';

@Component({
  selector: 'app-add-workshops',
  standalone: true,
  imports: [
    CommonModule,
    WorkshopListComponent,
    WorkshopMapComponent,
    WorkshopDetailComponent
  ],
  templateUrl: './add-workshops.component.html',
  styleUrl: './add-workshops.component.scss'
})
export class AddWorkshopsComponent implements OnChanges, OnDestroy {
  @Input() searchTerm: string = '';
  @Input() accountFilter: AccountFilterData = {} as AccountFilterData;

  isLoading = true;
  isDeleting = false;
  isSaving = false;
  isDetailsLoading = true;
  workshops: WorkshopsResponse[] = [];
  filteredWorkshops: WorkshopsResponse[] = [];
  workshopLocations: WorkshopLocationResponse[] = [];
  filteredWorkshopLocations: WorkshopLocationResponse[] = [];
  selectedWorkshop: WorkshopsResponse | null = null;
  workshopDetails: WorkshopDetailsResponse;
  viewState: 'map' | 'edit' | 'add' = 'map';
  
  private workshopService = inject(WorkshopService);
  private errorHandlerService = inject(ErrorHandlerService);
  private confirmationModalService = inject(ConfirmationModalService);
  private readonly toastr = inject(ToastrService);
  private readonly transloco = inject(TranslocoService);
  private cancelSubject = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchTerm']) {
      this.filterWorkshops();
    }

    if (changes['accountFilter'] && this.hasValidAccountFilter()) {
      this.loadWorkshops();
    }
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async onSelectWorkshop(workshop: WorkshopsResponse): Promise<void> {
    if (this.selectedWorkshop?.id === workshop.id && this.viewState !== 'map') {
      this.selectedWorkshop = null;
      this.viewState = 'map';
      return;
    }

    this.viewState = 'edit';
    this.isDetailsLoading = true;

    try {
      this.cancelSubject.next();
      const response = await this.workshopService.getWorkshopDetails(workshop.id, this.cancelSubject);
      this.workshopDetails = response.data ?? {} as WorkshopDetailsResponse;
      this.selectedWorkshop = workshop;

      this.isDetailsLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: async () => {
          this.selectedWorkshop = null;
          this.viewState = 'map';
          this.isDetailsLoading = false;
        }
      });
    }
  }

  async onSave(workshop: WorkshopDetailsRequest): Promise<void> {
    this.isSaving = true;

    try {
      if (this.viewState === 'edit') {
        await this.workshopService.saveWorkshop(workshop);
        await this.loadWorkshops();

        this.cancelSubject.next();
        this.workshopDetails = (await this.workshopService.getWorkshopDetails(workshop.id, this.cancelSubject)).data ?? {} as WorkshopDetailsResponse;
      } else {
        const response = await this.workshopService.saveWorkshop(workshop);
        await this.loadWorkshops();

        this.cancelSubject.next();
        this.selectedWorkshop = this.workshops.find(w => w.id === response.data?.workshopId) ?? null;
        this.workshopDetails = (await this.workshopService.getWorkshopDetails(response.data?.workshopId ?? 0, this.cancelSubject)).data ?? {} as WorkshopDetailsResponse;
        this.viewState = 'edit';
      }

      this.toastr.success(this.transloco.translate('assetSettings.workshops.toasts.saved'));
      this.isSaving = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: async () => {
          this.selectedWorkshop = null;
          this.viewState = 'map';
          await this.loadWorkshops();
          this.isSaving = false;
        }
      });
    }
  }

  onDelete(workshop: WorkshopDetailsResponse): void {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('assetSettings.workshops.delete.title'),
      message: this.transloco.translate('assetSettings.workshops.delete.message', { name: workshop.name }),
      warningMessage: this.transloco.translate('assetSettings.workshops.delete.warning'),
      confirmText: this.transloco.translate('assetSettings.common.yesDelete'),
      declineText: this.transloco.translate('assetSettings.common.noCancel'),
      confirmPlaceholder: this.transloco.translate('assetSettings.common.typeDeleteToConfirm'),
      confirmationPrompt: 'delete',
      onConfirm: () => this.deleteWorkshop(workshop)
    };

    this.confirmationModalService.showConfirmationModal(options);
  }

  private async deleteWorkshop(workshop: WorkshopDetailsResponse): Promise<void> {
    this.isDeleting = true;
    this.isLoading = true;

    try {
      const request: DeleteWorkshopRequest = {
        id: workshop.id,
        accountId: workshop.accountId
      }

      await this.workshopService.deleteWorkshop(request);
      await this.loadWorkshops();
      this.selectedWorkshop = null;
      this.viewState = 'map';

      this.isDeleting = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: async () => {
          this.selectedWorkshop = null;
          this.viewState = 'map';
          await this.loadWorkshops();
          this.isDeleting = false;
        }
      });
    }
  }

  async loadWorkshops(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const [locationsResponse, workshopsResponse] = await Promise.all([
        this.workshopService.getWorkshopLocations(this.accountFilter.accountIds, this.cancelSubject),
        this.workshopService.getWorkshops(this.accountFilter.accountIds, this.cancelSubject)
      ]);

      this.workshops = workshopsResponse.data ?? [];
      this.workshopLocations = locationsResponse.data ?? [];
      this.filterWorkshops();

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  onMarkerClicked(workshopId: number): void {
    const workshop = this.filteredWorkshops.find(w => w.id === workshopId);
    if (workshop) {
      this.selectedWorkshop = workshop;
    }
  }

  onAddNew(): void {
    this.selectedWorkshop = null;
    this.viewState = 'add';
  }

  onCancel(): void {
    if (this.viewState === 'add') {
      this.viewState = 'edit';
      this.selectedWorkshop = this.workshops.find(w => w.id === this.workshopDetails.id) ?? null;
    }
  }

  private hasValidAccountFilter(): boolean {
    return Array.isArray(this.accountFilter?.accountIds);
  }

  private filterWorkshops(): void {
    if (this.viewState === 'edit' && !this.isSaving) {
      this.viewState = 'map';
      this.selectedWorkshop = null;
    }

    let workshopResults = [...this.workshops];
    let workshopLocationResults = [...this.workshopLocations];

    if (this.searchTerm?.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      workshopResults = workshopResults.filter(w =>
        w.name.toLowerCase().includes(term)
      );
      workshopLocationResults = workshopLocationResults.filter(w =>
        w.name.toLowerCase().includes(term)
      );
    }

    this.filteredWorkshops = workshopResults;
    this.filteredWorkshopLocations = workshopLocationResults;
  }
}